/**
 * ConfirmationCard — templated Yes/No confirmation card for hosting and domain payments.
 * All copy is fixed (not LLM-generated).
 *
 * Tier 1 (completed/declined/stale): Full body visible, buttons disabled, status line.
 * Tier 2 (show_picker/usdc_pending): Collapsed to header + status line.
 * Active: Full body + live buttons.
 *
 * YES → calls /chat/payments/confirm (may show picker or charge directly).
 * NO → calls /chat/payments/decline.
 */

import React, { useState } from 'react';
import api from '../utils/api';

// Link 20 F1: hosting cards name the site they pay for (site_label), and renewals have their own card.
const forSite = (meta) => (meta.site_label ? ` for ${meta.site_label}` : '');
const longDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
};

const CONFIRMATION_TEMPLATES = {
  hosting: {
    header: (meta) => `🏠 Website Hosting${forSite(meta)} — ${meta.price_display || '$44.99'}/year`,
    body: (meta) => {
      const trialDays = meta.trial_started_at
        ? Math.max(0, 30 - Math.floor((Date.now() - new Date(meta.trial_started_at)) / 86400000))
        : 30;
      return (
        `Hosting is where your website files are stored on a server in the cloud so people can visit your site anytime. ` +
        `We offer the cheapest possible hosting rates at only ${meta.price_display || '$44.99'} per year.\n\n` +
        `Right now you have a complimentary 30-day free hosting trial (${trialDays} days remaining). ` +
        `Would you like to add proper hosting for your website? You still get the free trial time remaining plus one year.`
      );
    },
    yes_label: 'Yes, add hosting',
    no_label: 'No thanks',
  },
  hosting_trial_over: {
    header: (meta) => `🏠 Website Hosting${forSite(meta)} — ${meta.price_display || '$44.99'}/year`,
    body: (meta) =>
      `The free hosting trial${forSite(meta)} has ended. For ${meta.price_display || '$44.99'} per year, ` +
      `we'll keep the site online for a full year. That's the cheapest hosting you'll find anywhere.`,
    yes_label: 'Yes, add hosting',
    no_label: 'No thanks',
  },
  hosting_renewal: {
    header: (meta) => `🏠 Renew Hosting${forSite(meta)} — ${meta.price_display || '$44.99'}/year`,
    body: (meta) =>
      `Hosting${forSite(meta)} is paid through ${longDate(meta.paid_through)}. ` +
      `Renew now for ${meta.price_display || '$44.99'} and it runs through ${longDate(meta.new_paid_through)} — ` +
      `the new year starts when the current one ends, so you don't lose any time.`,
    yes_label: 'Yes, renew',
    no_label: 'Not now',
  },
  hosting_restore: {
    header: (meta) => `🏠 Restore Hosting${forSite(meta)} — ${meta.price_display || '$44.99'}/year`,
    body: (meta) =>
      `Your website was taken offline because the free trial ended. All your files are still saved — nothing was lost.\n\n` +
      `For ${meta.price_display || '$44.99'} per year, we'll bring your site back online immediately and keep it live for a full year. ` +
      `That's the cheapest hosting you'll find anywhere.`,
    yes_label: 'Yes, restore my site',
    no_label: 'No thanks',
  },
  domain: {
    header: (meta) => `🌐 ${meta.domain_name || 'Domain'} — ${meta.price_display || ''}`,
    body: (meta) =>
      `Right now your website is at ${meta.subdomain_url || 'your current address'}. ` +
      `A custom domain gives your site its own professional address that people can find and remember easily.\n\n` +
      `${meta.domain_name || 'Your domain'} is ${meta.price_display || ''} and includes DNS setup, SSL security, and privacy protection.`,
    yes_label: (meta) => `Yes, register ${meta.domain_name || 'this domain'}`,
    no_label: 'No thanks',
  },
};

const STATUS_LINES = {
  // 'completed' removed — the PaymentReceipt card now serves as the receipt
  declined: 'Declined',
  stale: 'Domain selection changed',
  show_picker: 'Choose your payment method below ↓',
  usdc_pending: 'Complete your USDC payment below ↓',
  card_pending: 'Add your card below ↓',
  superseded: 'Replaced by a newer card below',
};

function getCardType(metadata) {
  if (metadata.payment_type === 'domain') return 'domain';
  if (metadata.type === 'hosting_restore') return 'hosting_restore';
  if (metadata.type === 'hosting_renewal') return 'hosting_renewal';
  if (metadata.trial_started_at) {
    const trialDays = 30 - Math.floor((Date.now() - new Date(metadata.trial_started_at)) / 86400000);
    // Link 20 F1: an order-backed card (order_id) knows the site isn't archived — its trial just ended.
    if (trialDays <= 0) return metadata.order_id ? 'hosting_trial_over' : 'hosting_restore';
  } else if (metadata.order_id && metadata.type === 'hosting') {
    return 'hosting_trial_over';
  }
  return 'hosting';
}

export default function ConfirmationCard({ metadata, conversationId }) {
  const [cardState, setCardState] = useState(metadata?.card_state || null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const effectiveConversationId = conversationId || metadata?.conversation_id;
  const cardType = getCardType(metadata);
  const template = CONFIRMATION_TEMPLATES[cardType];

  if (!template) return null;

  const isTier1 = cardState === 'completed' || cardState === 'declined' || cardState === 'stale' || cardState === 'superseded';
  const isTier2 = cardState === 'show_picker' || cardState === 'usdc_pending' || cardState === 'card_pending';
  const isDisabled = isTier1 || isTier2;
  const statusLine = STATUS_LINES[cardState];

  const headerText = template.header(metadata);
  const bodyText = template.body(metadata);
  const yesLabel = typeof template.yes_label === 'function' ? template.yes_label(metadata) : template.yes_label;
  const noLabel = template.no_label;

  const handleYes = async () => {
    if (loading || isDisabled) return;
    setLoading(true);
    setMessage(null);
    try {
      const payload = {
        payment_type: metadata.payment_type,
        conversation_id: effectiveConversationId,
      };
      if (metadata.payment_type === 'hosting') {
        payload.site_deployment_id = metadata.site_deployment_id;
        if (metadata.order_id) payload.order_id = metadata.order_id;
      } else if (metadata.payment_type === 'domain') {
        payload.consultation_id = metadata.consultation_id;
        payload.selected_domain = metadata.selected_domain || metadata.domain_name;
      }

      const data = await api.post('/chat/payments/confirm', payload);

      switch (data.action) {
        case 'charged':
        case 'chat_update':
          setCardState('completed');
          break;
        case 'show_picker':
          setCardState('show_picker');
          break;
        case 'usdc_payment':
          setCardState('usdc_pending');
          break;
        case 'add_payment_method':
          setCardState('card_pending');
          break;
        case 'redirect':
          window.location.href = data.url;
          break;
        case 'need_method':
          setMessage(data.message || data.content);
          setLoading(false);
          break;
        case 'already_paid':
          setCardState('completed');
          setMessage(data.message);
          break;
        case 'stale_card':
          // A pre-F1 hosting card: the server posted an up-to-date one (Link 20 F1).
          setCardState(metadata.payment_type === 'hosting' ? 'superseded' : 'stale');
          setMessage(data.message);
          break;
        case 'error':
          setMessage(data.content || 'Something went wrong. Please try again.');
          setLoading(false);
          break;
        default:
          setMessage('Something went wrong. Please try again.');
          setLoading(false);
      }
    } catch (err) {
      setMessage(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleNo = async () => {
    if (loading || isDisabled) return;
    setLoading(true);
    try {
      const payload = {
        payment_type: metadata.payment_type,
        conversation_id: effectiveConversationId,
      };
      if (metadata.payment_type === 'hosting') {
        payload.site_deployment_id = metadata.site_deployment_id;
      } else if (metadata.payment_type === 'domain') {
        payload.consultation_id = metadata.consultation_id;
      }
      await api.post('/chat/payments/decline', payload);
      setCardState('declined');
    } catch (err) {
      setMessage(err.message || 'Something went wrong.');
      setLoading(false);
    }
  };

  return (
    <div className={`message-special confirmation-card${isDisabled ? ' confirmation-card--disabled' : ''}`}>
      <div className="payment-picker">
        {/* Header always visible */}
        <p className="picker-header">
          {headerText}
        </p>

        {/* Tier 2 hides body and buttons */}
        {!isTier2 && (
          <>
            <p className="picker-prompt" style={{ whiteSpace: 'pre-line' }}>
              {bodyText}
            </p>
            <div className="picker-options">
              <button
                className="picker-btn stripe-btn"
                onClick={handleYes}
                disabled={loading || isDisabled}
              >
                {loading ? '⏳ Processing...' : yesLabel}
              </button>
              <button
                className="picker-btn"
                onClick={handleNo}
                disabled={loading || isDisabled}
                style={{ background: 'transparent', border: '1px solid #666', color: '#999' }}
              >
                {noLabel}
              </button>
            </div>
          </>
        )}

        {/* Status line */}
        {statusLine && (
          <p className="picker-status">
            {statusLine}
          </p>
        )}

        {/* Error / info messages */}
        {message && <p className="picker-status">{message}</p>}
      </div>
    </div>
  );
}
